import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-8">
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-6 w-20 bg-muted/20 animate-pulse rounded-sm" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="font-serif text-lg tracking-widest uppercase text-foreground/90 hover:text-primary transition-colors duration-300">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="text-muted-foreground p-4">Loading navigation...</div>;
  }
  return <nav className="flex flex-col items-center gap-6 mt-12 w-full">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="font-serif text-2xl tracking-widest uppercase text-foreground hover:text-primary transition-colors w-full text-center py-2" onClick={onClose}>
          {item.label}
        </Link>)}
    </nav>;
}
export default function WildlingsWestHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="wildlings-west-header" className={`sticky top-0 z-50 w-full transition-all duration-500 border-b border-border/10 dark bg-background text-foreground ${isScrolled ? "bg-background/80 backdrop-blur-md shadow-lg" : "bg-background"}`}>
      <div className="container mx-auto px-4 h-20 flex items-center justify-between">
        {}
        <div className="flex-shrink-0">
          <Link to="/" className="flex items-center gap-3">
            <img src="https://wildlingswestoverland.com/wp-content/uploads/2026/03/Screenshot-2026-03-02-at-9.26.59-PM-2.png" alt="Wildlings West Overland" className="h-[49px]" />
          </Link>
        </div>

        {}
        <div className="hidden md:flex flex-1 justify-center">
          <MenuProvider menu_id="34" className="w-full flex justify-center">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="flex-shrink-0 w-[200px] flex justify-end items-center gap-4">
          <Button className="hidden md:inline-flex bg-primary hover:bg-primary/90 text-primary-foreground font-serif tracking-wider uppercase rounded-[2px] px-6 shadow-md hover:shadow-lg transition-all">
            Join the Convoy
          </Button>

          {}
          <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:text-primary hover:bg-transparent" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
            {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
          </Button>
        </div>
      </div>

      {}
      {isMobileMenuOpen && <div className="md:hidden fixed inset-0 top-20 z-40 bg-background/95 backdrop-blur-xl border-t border-border/10 animate-in fade-in slide-in-from-top-5 duration-200 h-[calc(100vh-5rem)] overflow-y-auto">
          <div className="container mx-auto px-4 py-8 flex flex-col items-center">
            <MenuProvider menu_id="34">
              <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>
            
            <div className="mt-12 w-full max-w-xs">
              <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-serif tracking-wider uppercase rounded-[2px] h-12 text-lg shadow-md">
                Join the Convoy
              </Button>
            </div>
          </div>
        </div>}
    </header>;
}